import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useToast } from './ToastContext';
import apiService from '../utils/apiService';
import {
  AFFILIATE_NOTIFICATION_EVENT,
  createAffiliateNotificationConnection,
} from '../utils/affiliateNotificationHub';
import {
  MAX_AFFILIATE_NOTIFICATIONS,
  countUnread,
  mergeNotifications,
  normalizeNotification,
  normalizeNotificationList,
} from '../utils/notificationData';

const NotificationContext = createContext(null);

export const CONNECTION_STATUS = {
  Connecting: 'Connecting',
  Connected: 'Connected',
  Disconnected: 'Disconnected',
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children, enabled = true }) => {
  const { showInfo } = useToast();
  const showInfoRef = useRef(showInfo);
  showInfoRef.current = showInfo;

  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.Disconnected);
  const [historyLoading, setHistoryLoading] = useState(false);

  const connectionRef = useRef(null);
  const intentionalStopRef = useRef(false);
  const retryTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const toastedIdsRef = useRef(new Set());
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const unreadCount = useMemo(() => countUnread(notifications), [notifications]);

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const upsertLive = useCallback((raw, { toast = true } = {}) => {
    const item = normalizeNotification({
      ...raw,
      isRead: false,
      deliveredViaSignalR: true,
    });

    setNotifications((prev) => {
      if (prev.some((n) => n.identity === item.identity)) return prev;
      return mergeNotifications([item], prev, MAX_AFFILIATE_NOTIFICATIONS);
    });

    if (toast && !toastedIdsRef.current.has(item.identity)) {
      toastedIdsRef.current.add(item.identity);
      const body = item.message || item.title;
      showInfoRef.current(body, 6000, item.message ? item.title : '');
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!enabledRef.current) return;
    setHistoryLoading(true);
    try {
      const res = await apiService.getAffiliateNotifications(50);
      if (!mountedRef.current) return;
      if (res.success) {
        const list = normalizeNotificationList(res.data);
        setNotifications((prev) => mergeNotifications(list, prev, MAX_AFFILIATE_NOTIFICATIONS));
      }
    } catch {
      // 401 handled by apiService → logout flow
    } finally {
      if (mountedRef.current) setHistoryLoading(false);
    }
  }, []);

  const markAllSeen = useCallback(() => {
    setNotifications((prev) => {
      if (prev.every((n) => n.isRead)) return prev;
      return prev.map((n) => (n.isRead ? n : { ...n, isRead: true }));
    });
    // Persist IsRead in Excel so refresh does not restore unread badge
    apiService.markAffiliateNotificationsRead(null).catch(() => {});
  }, []);

  const markOneSeen = useCallback((identity) => {
    setNotifications((prev) => {
      const target = prev.find((n) => n.identity === identity);
      if (!target || target.isRead) return prev;
      const id = Number(target.notificationId);
      if (Number.isFinite(id) && id > 0) {
        apiService.markAffiliateNotificationsRead([id]).catch(() => {});
      }
      return prev.map((n) => (n.identity === identity ? { ...n, isRead: true } : n));
    });
  }, []);

  const stopConnection = useCallback(async () => {
    intentionalStopRef.current = true;
    clearRetryTimer();
    const connection = connectionRef.current;
    connectionRef.current = null;
    if (connection) {
      try {
        connection.off(AFFILIATE_NOTIFICATION_EVENT);
        await connection.stop();
      } catch {
        // ignore stop errors
      }
    }
    if (mountedRef.current) {
      setConnectionStatus(CONNECTION_STATUS.Disconnected);
    }
  }, []);

  const startConnection = useCallback(async () => {
    if (!enabledRef.current) return;

    clearRetryTimer();
    intentionalStopRef.current = false;

    if (connectionRef.current) {
      const state = connectionRef.current.state;
      if (state === HubConnectionState.Connected || state === HubConnectionState.Connecting) {
        return;
      }
      try {
        await connectionRef.current.stop();
      } catch {
        // ignore
      }
      connectionRef.current = null;
    }

    const connection = createAffiliateNotificationConnection(() => apiService.getAuthToken());
    connectionRef.current = connection;

    connection.on(AFFILIATE_NOTIFICATION_EVENT, (payload) => {
      if (!mountedRef.current) return;
      upsertLive(payload, { toast: true });
    });

    connection.onreconnecting(() => {
      if (mountedRef.current) setConnectionStatus(CONNECTION_STATUS.Connecting);
    });

    connection.onreconnected(() => {
      if (mountedRef.current) setConnectionStatus(CONNECTION_STATUS.Connected);
    });

    connection.onclose(() => {
      if (!mountedRef.current) return;
      setConnectionStatus(CONNECTION_STATUS.Disconnected);
      if (intentionalStopRef.current || !enabledRef.current) return;
      clearRetryTimer();
      retryTimerRef.current = setTimeout(() => {
        if (mountedRef.current && !intentionalStopRef.current && enabledRef.current) {
          startConnection();
        }
      }, 5000);
    });

    setConnectionStatus(CONNECTION_STATUS.Connecting);
    try {
      await connection.start();
      if (mountedRef.current) setConnectionStatus(CONNECTION_STATUS.Connected);
    } catch {
      if (mountedRef.current) setConnectionStatus(CONNECTION_STATUS.Disconnected);
      if (!intentionalStopRef.current && enabledRef.current) {
        clearRetryTimer();
        retryTimerRef.current = setTimeout(() => {
          if (mountedRef.current && !intentionalStopRef.current && enabledRef.current) {
            startConnection();
          }
        }, 5000);
      }
    }
  }, [upsertLive]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      stopConnection();
      setNotifications([]);
      toastedIdsRef.current = new Set();
      return () => {
        mountedRef.current = false;
      };
    }

    fetchHistory();
    startConnection();

    return () => {
      mountedRef.current = false;
      stopConnection();
    };
    // Only re-run when auth shell enables/disables notifications
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      connectionStatus,
      historyLoading,
      fetchHistory,
      markAllSeen,
      markOneSeen,
      startConnection,
      stopConnection,
    }),
    [
      notifications,
      unreadCount,
      connectionStatus,
      historyLoading,
      fetchHistory,
      markAllSeen,
      markOneSeen,
      startConnection,
      stopConnection,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
  );
};

export default NotificationProvider;
